import { uiText, useUiLanguage } from '@/i18n/interface';
import warriorHubLogo from '@/assets/warriorhub-logo.png';
import React from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useTheme } from '@/contexts/ThemeContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Sun, Moon, Settings, LogOut, Bell, ChevronDown } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import StudentNotifications from './StudentNotifications';
import LanguageSelector from './LanguageSelector';

interface LayoutProps { children: React.ReactNode; showHeader?: boolean; title?: string; }

const Layout = ({ children, showHeader = true, title }: LayoutProps) => {
  useUiLanguage();
  const { profile, logout, impersonation, stopImpersonation } = useAuth();
  const { isDark, toggleTheme } = useTheme();
  const { t } = useTranslation('common');
  if (!showHeader) return <div className="wh-app">{children}</div>;
  const initials = `${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}` || 'WH';
  return <div className="wh-app">
    <a href="#main-content" className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-card focus:p-3">{t("common:skipToContent", { defaultValue: 'Skip to content' })}</a>
    <header className="sticky top-0 z-40 border-b bg-card/95 backdrop-blur-xl">
      <div className="mx-auto flex min-h-20 max-w-[1440px] flex-wrap items-center justify-between gap-3 px-4 py-3 sm:px-8">
        <Link to="/" className="flex min-w-0 items-center gap-3" aria-label={uiText("WarriorHub dashboard")}>
          <img src={warriorHubLogo} alt={uiText('WarriorHub Logo')} className="h-12 w-12 shrink-0 rounded-lg object-contain" />
          <div><span className="text-lg font-bold tracking-tight">{uiText("WarriorHub")}</span><p className="text-[11px] text-muted-foreground">{impersonation.active ? impersonation.dojoName : title || t("common:dashboard", { defaultValue: 'Dojo workspace' })}</p></div>
        </Link>
        <div className="flex items-center gap-1 sm:gap-3">
          {profile?.role === 'student' && <StudentNotifications />}
          <LanguageSelector />
          <Button variant="ghost" size="icon" onClick={toggleTheme} aria-label={isDark ? uiText("Switch to light mode") : uiText("Switch to dark mode")}>
            {isDark ? <Sun /> : <Moon />}
          </Button>
          {profile && <DropdownMenu>
            <DropdownMenuTrigger asChild><Button variant="ghost" className="gap-2 px-1 sm:px-2" aria-label={t("common:accountMenu", { defaultValue: 'Account menu' })}>
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-accent text-xs font-semibold text-accent-foreground">{initials}</span>
              <span className="hidden text-sm lg:inline">{profile.first_name}</span><ChevronDown className="hidden sm:block" />
            </Button></DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              <div className="break-words px-2 py-2"><p className="text-sm font-medium">{profile.first_name} {profile.last_name}</p><p className="mt-1 text-xs text-muted-foreground">{profile.email}</p></div>
              <DropdownMenuSeparator />
              {impersonation.active && <DropdownMenuItem onClick={stopImpersonation}><Settings className="mr-2 h-4 w-4" />{uiText("Stop impersonation")}</DropdownMenuItem>}
              <DropdownMenuItem onClick={() => logout()}><LogOut className="mr-2 h-4 w-4" />{t("common:logout", { defaultValue: 'Sign out' })}</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>}
        </div>
      </div>
    </header>
    <main id="main-content" className="min-w-0">{children}</main>
  </div>;
};
export default Layout;
